/**
 * App entry point
 *
 * - Loads `app.tsx`, which mounts the UI and emits "ui-ready"
 * - Waits for "ui-ready" before loading `main.app.ts` to ensure no events are missed
 * - Emits "load" after receiving the "initialized" event triggered by app.init()
 * - Initializes Sentry in production
 */

import emitter from "@webcore/core/events/eventemitter.ts";
import * as Sentry from "@sentry/react";

// TEMPORARY (do not commit): forces the blank holding page for a prod deploy.
// app.tsx and main.app.ts are never imported, so no Babylon, 3D assets or
// 8thWall are downloaded at all. Set back to false / revert this file and delete
// src/routes/ComingSoon.tsx once prod is meant to serve the real experience.
const comingSoon = true;

const hideInitialLoader = (delayMs = 100, fadeMs = 300) => {
  const loader = document.getElementById("initial-loader");
  if (!loader) return;
  if (loader.getAttribute("data-hidden") === "true") return;

  loader.setAttribute("data-hidden", "true");
  loader.style.transition = `opacity ${fadeMs}ms ease-out`;
  window.setTimeout(() => {
    loader.style.opacity = "0";
    window.setTimeout(() => {
      if (loader.parentNode) {
        loader.parentNode.removeChild(loader);
      }
    }, fadeMs);
  }, delayMs);
};

// Expose a Promise that resolves once main.app.ts has loaded so the UI can
// gate permission-triggering clicks (e.g. the AR Launch button) behind it.
// We still defer loading main.app.ts until ui-ready (to avoid running
// Babylon initialization before the canvas is laid out by React), but the
// Splash button reads this promise to refuse early clicks that would
// otherwise emit xr-start before the listener is registered.
let resolveMainAppReady: (value: boolean) => void;
(window as any).__kirraeagleMainAppReady = new Promise<boolean>((resolve) => {
  resolveMainAppReady = resolve;
});

let hasStartedMainApp = false;
const startMainApp = () => {
  if (hasStartedMainApp) return;
  hasStartedMainApp = true;
  import("./main.app.ts")
    .then(() => resolveMainAppReady(true))
    .catch((err) => {
      console.error("Failed to import main.app", err);
      resolveMainAppReady(false);
    });
};

if (comingSoon) {
  Promise.all([import("preact"), import("./routes/ComingSoon")])
    .then(([{ render }, { ComingSoon }]) => {
      render(<ComingSoon />, document.getElementById("app") as HTMLElement);
      hideInitialLoader();
    })
    .catch((error) => {
      console.error("Failed to import coming-soon page", error);
      hideInitialLoader();
    });
} else {
  import("./app").catch((error) => {
    console.error("Failed to import app shell", error);
  });

  emitter.on("ui-ready", () => {
    hideInitialLoader();
    startMainApp();
  });
}

//----------------------------------------------------------------

emitter.on("initialized", () => {

});

if (!import.meta.env.DEV) {
  Sentry.init({
    dsn: "https://c037deba1f0c6908418fd4d14285a6ba@o1388406.ingest.us.sentry.io/4511708924542976",
    maxBreadcrumbs: 20,
    integrations: [
      Sentry.breadcrumbsIntegration({
        console: false,
        dom: false,
      }),
    ],
    beforeBreadcrumb(breadcrumb) {
      try {
        if (JSON.stringify(breadcrumb).length > 5000) return null;
      } catch {
        return null;
      }
      return breadcrumb;
    },
  });
}
